import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import MainLayout from '../../components/layout/MainLayout';
import applicationApi from '../../services/api/application.api';
import { observabilityApi } from '../../services/api/observability.api';
import rumService from '../../services/api/rum.service';
import productAnalyticsService from '../../services/api/product-analytics.service';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { OnboardingHub } from '../../components/onboarding';
import { useOnboardingTour } from '../../contexts/OnboardingTourContext';

export default function DashboardPage() {
  const { t } = useTranslation(['common', 'dashboards']);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [applications, setApplications] = useState<any[]>([]);
  const [timeRange, setTimeRange] = useState('24h');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    applications: 0,
    activeAlerts: 0,
    tracesToday: 0,
    logsToday: 0,
    avgResponseTime: 0,
    errorRate: 0,
    uptime: 99.8,
    activeHosts: 0,
  });
  const [rumStats, setRumStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    webVitals: { lcp: 0, fid: 0, cls: 0 },
    frontendErrors: 0,
    affectedUsers: 0,
  });
  const [userEvents, setUserEvents] = useState({
    total: 0,
    topEvent: '',
    topEventCount: 0,
  });
  const [performanceData, setPerformanceData] = useState<any[]>([]);
  const [errorRateData, setErrorRateData] = useState<any[]>([]);
  const [serviceHealthData, setServiceHealthData] = useState<any[]>([]);
  const [recentActivityData, setRecentActivityData] = useState<any[]>([]);
  const [healthData, setHealthData] = useState<any>(null);
  const [insightsData, setInsightsData] = useState<{ recentIncidents: any[]; topAnomalies: any[] }>({ recentIncidents: [], topAnomalies: [] });
  const [overviewExtra, setOverviewExtra] = useState<any>({});
  const { startTour } = useOnboardingTour();

  useEffect(() => {
    loadDashboardData();
  }, [timeRange, selectedAppId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Clear previous data to avoid showing stale data
      setPerformanceData([]);
      setErrorRateData([]);
      setServiceHealthData([]);
      setRecentActivityData([]);

      console.log('[DashboardPage] Fetching unified dashboard data:', { timeRange, selectedAppId });

      // Load all dashboard data from single unified endpoint
      const dashboardData = await observabilityApi.getDashboard(timeRange, selectedAppId || undefined);

      console.log('[DashboardPage] Dashboard data received:', dashboardData);

      // Update all stats from unified data
      setStats({
        applications: dashboardData.overview.totalApplications,
        activeAlerts: dashboardData.overview.activeAlerts,
        tracesToday: dashboardData.overview.tracesToday,
        logsToday: dashboardData.overview.logsToday,
        avgResponseTime: dashboardData.overview.avgResponseTime,
        errorRate: dashboardData.overview.errorRate,
        uptime: 99.8,
        activeHosts: dashboardData.overview.totalHosts,
      });

      // Update chart data
      setPerformanceData(dashboardData.performance.data);
      setErrorRateData(dashboardData.errorRate.data);
      setServiceHealthData(dashboardData.serviceHealth);
      setRecentActivityData(dashboardData.recentActivity);

      // Dados acionáveis que o endpoint já entrega (mas não eram mostrados)
      setHealthData((dashboardData as any).health || null);
      setInsightsData({
        recentIncidents: (dashboardData as any).insights?.recentIncidents || [],
        topAnomalies: (dashboardData as any).insights?.topAnomalies || [],
      });
      setOverviewExtra({
        activeIncidents: (dashboardData as any).overview?.activeIncidents || 0,
        criticalAlerts: (dashboardData as any).overview?.criticalAlerts || 0,
        activeAnomalies: (dashboardData as any).overview?.activeAnomalies || 0,
      });

      // Update RUM stats
      setRumStats({
        totalSessions: dashboardData.rum.totalSessions,
        activeSessions: dashboardData.rum.activeSessions,
        frontendErrors: dashboardData.rum.frontendErrors,
        affectedUsers: dashboardData.rum.affectedUsers,
        webVitals: dashboardData.rum.webVitals,
      });

      // Load applications list separately (still needed for navigation)
      const appsResponse = await applicationApi.getAll();
      if (appsResponse.success) {
        setApplications(appsResponse.data);
      }

      // Keep Product Analytics as separate call (not yet in unified endpoint)
      if (appsResponse.success && appsResponse.data.length > 0) {
        const endTime = Date.now();
        const timeRangeMs: Record<string, number> = {
          '1h': 60 * 60 * 1000,
          '6h': 6 * 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
          '30d': 30 * 24 * 60 * 60 * 1000,
        };
        const startTime = endTime - (timeRangeMs[timeRange] || timeRangeMs['24h']);
        const appIds = selectedAppId ? [selectedAppId] : appsResponse.data.map((app: any) => app.id);

        // Load Product Analytics events 🎯
        try {
          console.log('[DashboardPage] Loading Product Analytics events for apps:', appIds);
          let totalEvents = 0;
          const eventCounts = new Map();

          for (const appId of appIds) {
            try {
              console.log('[DashboardPage] Fetching events for appId:', appId);
              const eventsResponse = await productAnalyticsService.queryEvents({
                applicationId: appId,
                startTime: new Date(startTime).toISOString(),
                endTime: new Date(endTime).toISOString(),
                limit: 1000,
              });

              console.log('[DashboardPage] Events response for', appId, ':', eventsResponse);

              if (eventsResponse && eventsResponse.events) {
                totalEvents += eventsResponse.events.length;

                // Count event types
                eventsResponse.events.forEach((event: any) => {
                  const name = event.event_name || 'unknown';
                  eventCounts.set(name, (eventCounts.get(name) || 0) + 1);
                });
              }
            } catch (err) {
              console.error('[DashboardPage] Error loading events for app', appId, ':', err);
            }
          }

          // Find top event
          let topEvent = '';
          let topEventCount = 0;
          for (const [name, count] of eventCounts.entries()) {
            if (count > topEventCount) {
              topEvent = name;
              topEventCount = count;
            }
          }

          console.log('[DashboardPage] Total events:', totalEvents, 'Top event:', topEvent, 'Count:', topEventCount);

          setUserEvents({
            total: totalEvents,
            topEvent: topEvent || 'page_view',
            topEventCount,
          });
        } catch (error) {
          console.error('Error loading analytics events:', error);
        }
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    num = parseInt("" + num);
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Web Vitals thresholds based on Google standards
  const getWebVitalColor = (metric: 'lcp' | 'fid' | 'cls', value: number) => {
    if (metric === 'lcp') {
      // Good: <=2.5s, Needs Improvement: <=4s, Poor: >4s
      if (value <= 2500) return { color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/20', label: t('common:dashboard.webVitals.good') };
      if (value <= 4000) return { color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/20', label: t('common:dashboard.webVitals.needsImprovement') };
      return { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/20', label: t('common:dashboard.webVitals.poor') };
    }
    if (metric === 'fid') {
      // Good: <=100ms, Needs Improvement: <=300ms, Poor: >300ms
      if (value <= 100) return { color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/20', label: t('common:dashboard.webVitals.good') };
      if (value <= 300) return { color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/20', label: t('common:dashboard.webVitals.needsImprovement') };
      return { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/20', label: t('common:dashboard.webVitals.poor') };
    }
    // CLS
    // Good: <=0.1, Needs Improvement: <=0.25, Poor: >0.25
    if (value <= 0.1) return { color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/20', label: t('common:dashboard.webVitals.good') };
    if (value <= 0.25) return { color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/20', label: t('common:dashboard.webVitals.needsImprovement') };
    return { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/20', label: t('common:dashboard.webVitals.poor') };
  };

  // Onboarding dispensável (não fica nagando depois de fechado)
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem('dashboard_onboarding_dismissed') === '1'
  );
  const dismissOnboarding = () => {
    localStorage.setItem('dashboard_onboarding_dismissed', '1');
    setOnboardingDismissed(true);
  };

  // Uptime real (média da saúde dos serviços) em vez de valor fixo
  const computedUptime = serviceHealthData.length
    ? serviceHealthData.reduce((sum, s) => sum + (Number(s.status) || 0), 0) / serviceHealthData.length
    : stats.uptime;

  // Status geral para decisão num relance
  const healthStatus: 'healthy' | 'warning' | 'critical' =
    stats.activeAlerts > 0 || stats.errorRate > 5 ? 'critical' : stats.errorRate > 1 ? 'warning' : 'healthy';
  const healthMeta = {
    healthy: { label: t('common:dashboard.allSystemsOperational', 'Tudo operacional'), ring: 'border-green-500/30', dot: 'bg-green-500', text: 'text-green-700 dark:text-green-300', glow: 'from-green-500/10' },
    warning: { label: t('common:dashboard.attention', 'Requer atenção'), ring: 'border-amber-500/30', dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-300', glow: 'from-amber-500/10' },
    critical: { label: t('common:dashboard.issuesDetected', 'Problemas detectados'), ring: 'border-red-500/40', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-300', glow: 'from-red-500/10' },
  }[healthStatus];

  // Cores por severidade dos KPIs de decisão
  const respColor = stats.avgResponseTime <= 500 ? 'text-green-600 dark:text-green-400' : stats.avgResponseTime <= 1000 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  const errColor = stats.errorRate <= 1 ? 'text-green-600 dark:text-green-400' : stats.errorRate <= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  const alertColor = stats.activeAlerts === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
  const upColor = computedUptime >= 99 ? 'text-green-600 dark:text-green-400' : computedUptime >= 95 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';

  const currentAppName = applications.find((a) => a.id === selectedAppId)?.name || t('common:labels.allApplications', 'Todas as Aplicações');

  // Saúde por pilar (APM / Infra / Kubernetes / Cloud) — status que o endpoint já entrega
  const pillarMeta = (status?: string) => {
    switch (status) {
      case 'down': return { dot: 'bg-red-500', text: 'text-red-600 dark:text-red-400', label: t('common:dashboard.statusDown', 'Fora do ar') };
      case 'degraded': return { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: t('common:dashboard.statusDegraded', 'Degradado') };
      case 'healthy': return { dot: 'bg-green-500', text: 'text-green-600 dark:text-green-400', label: t('common:dashboard.statusHealthy', 'Operacional') };
      default: return { dot: 'bg-gray-300 dark:bg-gray-600', text: 'text-gray-400', label: '—' };
    }
  };
  // Humaniza nomes de métrica e descreve a anomalia de forma conclusiva
  const METRIC_LABELS: Record<string, string> = {
    response_time: 'Tempo de resposta', response_time_avg: 'Tempo médio de resposta', error_rate: 'Taxa de erro',
    throughput: 'Throughput', request_count: 'Requisições', latency: 'Latência', latency_p95: 'Latência P95', latency_p99: 'Latência P99',
    cpu_usage: 'Uso de CPU', memory_usage: 'Uso de memória', disk_usage: 'Uso de disco',
  };
  const humanMetric = (m?: string) => (m && METRIC_LABELS[m]) || (m || '').replace(/_/g, ' ');
  const anomalyDetail = (a: any) => {
    const dev = a?.deviation_percent != null
      ? `${Math.abs(Math.round(Number(a.deviation_percent)))}% ${Number(a.deviation_percent) >= 0 ? t('common:dashboard.above', 'acima') : t('common:dashboard.below', 'abaixo')}`
      : '';
    if (a?.actual_value != null && a?.expected_value != null) {
      return `${Math.round(Number(a.actual_value))} ${t('common:dashboard.vsExpected', 'vs esperado')} ${Math.round(Number(a.expected_value))}${dev ? ` · ${dev}` : ''}`;
    }
    return dev || a?.description || a?.context || '';
  };

  const pillars = healthData ? [
    { key: 'apmStatus', label: 'APM', to: '/apm' },
    { key: 'infrastructureStatus', label: t('common:nav.infrastructure', 'Infraestrutura'), to: '/infrastructure/hosts' },
    { key: 'kubernetesStatus', label: 'Kubernetes', to: '/infrastructure/kubernetes' },
    { key: 'cloudStatus', label: t('common:nav.cloudScan', 'Cloud'), to: '/cloud-scan' },
  ] : [];

  return (
    <MainLayout>
      {/* Header with Filters */}
      <div className="mb-8" data-tour="dashboard-header">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('common:dashboard.welcomeMessage')}, {user?.firstName}! 👋
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('common:dashboard.subtitle')}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="w-full sm:w-48">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('common:dashboard.timeRange')}
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                disabled={loading}
              >
                <option value="1h">{t('common:dashboard.lastHour')}</option>
                <option value="6h">{t('common:dashboard.last6Hours')}</option>
                <option value="24h">{t('common:dashboard.last24Hours')}</option>
                <option value="7d">{t('common:dashboard.last7Days')}</option>
                <option value="30d">{t('common:dashboard.last30Days')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Status geral — decisão num relance */}
      <div className={`mb-6 overflow-hidden rounded-2xl border ${healthMeta.ring} bg-gradient-to-r ${healthMeta.glow} to-transparent`}>
        <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${healthMeta.dot} opacity-60`}></span>
              <span className={`relative inline-flex h-3 w-3 rounded-full ${healthMeta.dot}`}></span>
            </span>
            <div>
              <p className={`text-lg font-bold ${healthMeta.text}`}>{healthMeta.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{currentAppName}</p>
            </div>
          </div>
          {/* Métricas-chave de decisão */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:flex lg:items-center lg:gap-8">
            <button onClick={() => navigate('/alerts')} className="text-left">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('common:dashboard.activeAlerts', 'Alertas ativos')}</p>
              <p className={`text-xl font-bold ${alertColor}`}>{stats.activeAlerts}</p>
            </button>
            <button onClick={() => navigate('/apm/errors')} className="text-left">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('common:dashboard.errorRate', 'Taxa de erro')}</p>
              <p className={`text-xl font-bold ${errColor}`}>{stats.errorRate}%</p>
            </button>
            <button onClick={() => navigate('/apm')} className="text-left">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('common:dashboard.responseTime', 'Tempo de resposta')}</p>
              <p className={`text-xl font-bold ${respColor}`}>{stats.avgResponseTime}ms</p>
            </button>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('common:dashboard.uptimeLabel', 'Uptime')}</p>
              <p className={`text-xl font-bold ${upColor}`}>{computedUptime.toFixed(1)}%</p>
            </div>
            <button onClick={() => navigate('/analytics/rum')} className="text-left">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('common:dashboard.rum.active', 'Sessões ativas')}</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{rumStats.activeSessions}</p>
            </button>
          </div>
        </div>
      </div>

      {/* Saúde por pilar — onde está o problema, num relance */}
      {pillars.length > 0 && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {pillars.map((p) => {
            const m = pillarMeta(healthData?.[p.key]);
            return (
              <button
                key={p.key}
                onClick={() => navigate(p.to)}
                className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 text-left transition-shadow hover:shadow-sm dark:border-gray-800 dark:bg-gray-900"
              >
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{p.label}</p>
                  <p className={`mt-0.5 text-sm font-semibold ${m.text}`}>{m.label}</p>
                </div>
                <span className="relative flex h-2.5 w-2.5">
                  {healthData?.[p.key] === 'healthy' && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${m.dot} opacity-50`}></span>}
                  <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${m.dot}`}></span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Stats Grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Applications */}
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900">
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-indigo-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.applications', 'Applications')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.applications}</p>
                <p className="mt-2 flex items-center text-sm text-green-600 dark:text-green-400">
                  <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t('common:dashboard.allSystemsOperational')}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/20">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Active Alerts */}
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900">
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-red-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.activeAlerts', 'Active Alerts')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.activeAlerts}</p>
                <p className={`mt-2 flex items-center text-sm ${alertColor}`}>
                  <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={stats.activeAlerts === 0 ? 'M5 13l4 4L19 7' : 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'} />
                  </svg>
                  {stats.activeAlerts === 0
                    ? t('common:dashboard.allSystemsOperational', 'Tudo operacional')
                    : `${stats.activeAlerts} ${t('common:dashboard.active', 'ativos')}`}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Traces Today */}
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900">
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-blue-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.tracesToday', 'Traces Today')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(stats.tracesToday)}</p>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('common:dashboard.inPeriod', 'no período selecionado')}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Logs Today */}
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md dark:border-gray-800 dark:bg-gray-900">
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-green-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.logsToday', 'Logs Today')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(stats.logsToday)}</p>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('common:dashboard.inPeriod', 'no período selecionado')}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/20">
                <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RUM & Analytics - Compact Section */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* RUM Sessions with Web Vitals */}
        <div
          className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md cursor-pointer dark:border-gray-800 dark:bg-gray-900"
          onClick={() => navigate('/analytics/rum')}
        >
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-purple-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.rum.title')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(rumStats.totalSessions)}</p>
                <p className="mt-2 flex items-center text-sm">
                  <span className="flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-green-600 dark:text-green-400">{rumStats.activeSessions} {t('common:dashboard.rum.active')}</span>
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">LCP:</span>
                <span className={`font-semibold ${getWebVitalColor('lcp', rumStats.webVitals.lcp).color}`}>
                  {rumStats.webVitals.lcp}ms
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Frontend Errors */}
        <div
          className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md cursor-pointer dark:border-gray-800 dark:bg-gray-900"
          onClick={() => navigate('/analytics/rum')}
        >
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-orange-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.frontendErrors.title')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(rumStats.frontendErrors)}</p>
                <p className="mt-2 flex items-center text-sm text-orange-600 dark:text-orange-400">
                  <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {rumStats.affectedUsers} {t('common:dashboard.frontendErrors.affectedUsers')}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/20">
                <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* User Events (Product Analytics) */}
        <div
          className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md cursor-pointer dark:border-gray-800 dark:bg-gray-900"
          onClick={() => navigate('/analytics/events')}
        >
          <div className="absolute right-0 top-0 h-32 w-32 translate-x-8 -translate-y-8 transform rounded-full bg-pink-500 opacity-10 transition-transform group-hover:scale-150"></div>
          <div className="relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('common:dashboard.userEvents.title')}
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(userEvents.total)}</p>
                <p className="mt-2 flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <svg className="mr-1 h-4 w-4 text-pink-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  {t('common:dashboard.userEvents.topEvent')}: {userEvents.topEvent}
                </p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-pink-100 dark:bg-pink-900/20">
                <svg className="h-6 w-6 text-pink-600 dark:text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Hub (dispensável — não fica reaparecendo depois de fechado) */}
      {!onboardingDismissed && (
        <div className="relative mb-8" data-tour="onboarding-hub">
          <button
            onClick={dismissOnboarding}
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            title={t('common:dashboard.dismiss', 'Dispensar')}
            aria-label={t('common:dashboard.dismiss', 'Dispensar')}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <OnboardingHub variant="full" onStartTour={startTour} />
        </div>
      )}

      {/* Charts Grid */}
      {loading ? (
        <div className="mb-8 flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Performance Overview */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('common:dashboard.apmResponseTime')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:dashboard.apmResponseTimeDesc')}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/20 dark:text-green-400">
                  <span className="mr-1 h-1.5 w-1.5 rounded-full bg-green-500"></span>
                  {t('common:dashboard.apmTraces')}
                </span>
              </div>
            </div>
            {performanceData.length > 0 && (
              <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/50">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('common:dashboard.avgResponse', 'Tempo médio')}</p>
                  <p className={`text-lg font-bold ${respColor}`}>{stats.avgResponseTime}ms</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('common:dashboard.peak', 'Pico')}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{Math.round(Math.max(...performanceData.map((d) => d.responseTime || 0)))}ms</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('common:dashboard.totalRequests', 'Total de requisições')}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{formatNumber(performanceData.reduce((s, d) => s + (d.requests || 0), 0))}</p>
                </div>
              </div>
            )}
            {performanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                  <XAxis dataKey="time" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                  <YAxis yAxisId="left" stroke="#9CA3AF" label={{ value: t('common:dashboard.responseTimeMs'), angle: -90, position: 'insideLeft' }} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9CA3AF" label={{ value: t('common:dashboard.requests'), angle: 90, position: 'insideRight' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#F9FAFB',
                    }}
                  />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="responseTime" stroke="#6366F1" strokeWidth={2} name={t('common:dashboard.responseTimeMs')} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="requests" stroke="#10B981" strokeWidth={2} name={t('common:dashboard.requests')} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-500 dark:text-gray-400">
                <svg className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-sm">{t('common:dashboard.noTracesAvailable')}</p>
                <p className="text-xs mt-2">{t('common:dashboard.configureApm')}</p>
              </div>
            )}
          </div>

          {/* Error Rate from Logs */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('common:dashboard.logErrorRate')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:dashboard.logErrorRateDesc')}</p>
                </div>
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {t('common:dashboard.logs')}
                </span>
              </div>
            </div>
            {errorRateData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={errorRateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                  <XAxis dataKey="name" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#9CA3AF" label={{ value: `${t('common:dashboard.errorRate')} (%)`, angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '0.5rem',
                      color: '#F9FAFB',
                    }}
                    formatter={(value: any) => [`${value}%`, t('common:dashboard.errorRate')]}
                  />
                  <Bar dataKey="value" fill="#EF4444" name={`${t('common:dashboard.errorRate')} (%)`} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] text-gray-500 dark:text-gray-400">
                <svg className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm">{t('common:dashboard.noLogsAvailable')}</p>
                <p className="text-xs mt-2">{t('common:dashboard.configureLogs')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Health & Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Service Health */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('common:dashboard.serviceHealthTitle')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:dashboard.serviceHealthDesc')}</p>
              </div>
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                {t('common:dashboard.apmTraces')}
              </span>
            </div>
          </div>
          <div className="space-y-4">
            {serviceHealthData.length > 0 ? serviceHealthData.map((service) => (
              <div key={service.id} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{service.name}</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{service.status}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        service.status >= 99 ? 'bg-green-500' : service.status >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${service.status}%` }}
                    ></div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {service.incidents} {service.incidents === 1 ? t('common:dashboard.error') : t('common:dashboard.errors')} {t('common:dashboard.detectedInPeriod')}
                  </p>
                </div>
              </div>
            )) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                <svg className="h-12 w-12 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">{t('common:dashboard.noApplications')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <h3 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">{t('common:dashboard.recentActivity')}</h3>
          <div className="space-y-4">
            {recentActivityData.length > 0 ? recentActivityData.slice(0, 4).map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
                <div className={`flex-shrink-0 rounded-lg p-2 ${
                  activity.severity === 'critical' ? 'bg-red-100 dark:bg-red-900/20' :
                  activity.severity === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/20' :
                  activity.severity === 'success' ? 'bg-green-100 dark:bg-green-900/20' :
                  'bg-blue-100 dark:bg-blue-900/20'
                }`}>
                  {activity.type === 'alert' && (
                    <svg className="h-4 w-4 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {activity.type === 'error' && (
                    <svg className="h-4 w-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  {activity.type === 'deploy' && (
                    <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  )}
                  {activity.type === 'success' && (
                    <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.message}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{activity.service} • {activity.time}</p>
                </div>
              </div>
            )) : (insightsData.topAnomalies.length > 0 || insightsData.recentIncidents.length > 0) ? (
              <>
                {insightsData.topAnomalies.slice(0, 3).map((a: any, i: number) => (
                  <button key={`an-${i}`} onClick={() => navigate('/ml/anomalies')} className="flex w-full items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-left transition-colors hover:bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/10">
                    <div className="flex-shrink-0 rounded-lg bg-amber-100 p-2 dark:bg-amber-900/30">
                      <svg className="h-4 w-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {t('common:dashboard.anomalyIn', 'Anomalia em')} {humanMetric(a.metric_name) || t('common:dashboard.anomalyDetected', 'Anomalia detectada')}
                      </p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{anomalyDetail(a)}</p>
                    </div>
                  </button>
                ))}
                {insightsData.recentIncidents.slice(0, 3).map((inc: any, i: number) => (
                  <button key={`inc-${i}`} onClick={() => navigate('/alerts')} className="flex w-full items-start gap-3 rounded-lg border border-red-200 bg-red-50/50 p-3 text-left transition-colors hover:bg-red-50 dark:border-red-900/40 dark:bg-red-900/10">
                    <div className="flex-shrink-0 rounded-lg bg-red-100 p-2 dark:bg-red-900/30">
                      <svg className="h-4 w-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">{inc.title || inc.name || inc.message || t('common:dashboard.incident', 'Incidente')}</p>
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{inc.description || inc.service || inc.application_name || ''}</p>
                    </div>
                  </button>
                ))}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">{t('common:dashboard.allCalm', 'Tudo tranquilo')}</p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('common:dashboard.noIncidents', 'Sem incidentes ou anomalias no período')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
